const chemicalNomenclature = {
  status: 'Active',
  name: 'Chemical Nomenclature',
  imageFileName: 'images/nomenclature-icon.jpg',
  imageAlt:
    'Two rows of content. The top row represents the systematic naming of a molecular compound: The word "prefix" has a short blue line extending to its right meeting a blue box with curved edges that says "first elemnt". To the right of this is another word "prefix" that has a short blue line extending to its right meeting a blue box with curved edges that says "second elemnt". A blue line extends from its right side and the ending appears as "ide". The bottom row represents the systematic naming of an ionic compound: Describing the image left to right. A green box with curved edges reads "first element". A line extends from its right side and a light green box enveloped in a parenthesis reads "Possible Roman Numberal". Another green box with curved edges is to the right and reads "second element". A line extends from its right side and the ending appears as "ide".',

  description:
    'How to name systematically different types of chemical compounds.',
  tags: ['Dry Lab', 'Tutorial'],
  page: 'nomenclature',
  content: {
    overview: {
      name: 'Overview',
      link: 'overview',
      tabs: [
        {
          name: 'NOTAB',
          order: 1,
          steps: [
            {
              name: 'NOSTEP',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Text',
                  text: "To name a chemical requires one must know what type of chemical it is. This activity will review how to name four categories of chemicals:<ul><li> Ionic compounds</li><li>Molecular compounds</li><li>Hydrates</li><li>Acids</li></ul><p>Use the <b><em>Tutorial</em></b> to review the nomenclature of chemical compounds.</p>",
                },
              ],
            },
          ],
        },
      ],
    },
    tutorial: {
      name: 'Tutorial',
      link: 'tutorial',
      description: 'tbd',
      tabs: [
        {
          name: 'Ions',
          order: 1,
          steps: [
            {
              name: 'Ions',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Ions',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'If an atom has a charge, it is because it has lost or gained one or more electrons. Then the charged entity is called an ion. Metals atoms can become positive in charge as they typically lose electrons and are called cations. Nonmetals can become negative in charge as they typically gain electrons and are called anions. ',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/ion-formation.jpg',
                  imageAlt:
                    'The formation of two ions, the cation and the anion are presented. The cation example in on the left and the anion example is on the right. For the cation formation, a sodium atom symbol (capital n and lowercase a), labeled as sodium atom, has an arrow going to the right pointing to the sodium atom symbol with a superscript plus sign, labeled as sodium ion, and one electron represented as a lowercase e with an accent. For the anion formation, a chlorine atom symbol (capital c and lowercase l), labeled as chlorine atom, plus one electron represented as a lowercase e with an accent, has an arrow going to the right point to the chlorine atom symbol with a superscript negative sign.',
                  centered: true,
                },
              ],
            },
            {
              name: 'Monatomic Ions',
              order: 2,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Monatomic Ions',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: "Monatomic ions refer to one single atom having a charge, whether positive or negative. The value of the charge is dependent on the element itself. Anions will only carry one value. Meanwhile, the value of a cation could be single or variable. What charge a metal element takes on is based on its location in the periodic table: the main group elements, the transition metal elements and the post-transition metal elements.<p>Let's look at this further.</p>",
                },
              ],
            },
            {
              name: 'Main Group Elements',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Main Group Elements',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'In the periodic table below, the elements in the red boxes are highlighted as the main group elements. Main group elements can also be identified by numbering the group columns 1 through 8 (as seen in the figure in red). This numbering system is helpful to correlate for those metal elements that carry an expected single charge. All group 1 elements will lose 1 electron and hence have a 1+ charge. All group 2 elements will lose 2 electrons and hence have a 2+ charge. Group 3 elements that are metals can be expected to lose 3 electrons and hence have a 3+ charge. Once reaching group 4, non-metals are becoming prevalent and thus there is an expected gain of electrons. The gain of electrons caps at the number 8. Therefore, group 4 elements could gain 4 electrons and hence have a 4- charge. Group 5 elements gain 3 electrons and have a 3- charge. Group 6 elements gain 2 electrons and have 2- charge and last group 7 gain 1 electron becoming 1- charge.',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/main-group-elements.jpg',
                  imageAlt:
                    'A periodic table is presented with elements in their ionic form in their respective location in the table. The columns 1 though 8 are highlighted. The first two columns on the left of the table are labeled 1 and 2 and a red box surrounds it. An example of an element in its ionic form is hydrogen as H^+ for the first element in the table, located in column 1. An example of element in its ionic form located in column 2 is Ba with superscript two plus. To the right of the table are columns labeled 3 to 8. These columns are surrounded by a red box as well, but is not rectangular. On the bottom left of the boxed area, the red box does not include post-transition elements such as tin (Sn), lead (Pb) and bisuth (Bi). All elements that are listed in symbols are in ionic form. An example is column 3, second row, aluminum is presented as Al^3+.',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Header',
                  text: 'Naming of Cations',
                },
                {
                  order: 5,
                  type: 'Text',
                  text: 'All <b>metal</b> ions in the main group are named as the element itself followed by the word “ion”.',
                },
                {
                  id: 'q-001',
                  order: 6,
                  type: 'Input',
                  directions:
                    'Write the name for the following main group cations:',
                  equation: 'Ba<sup>2+</sup>',
                  validAnswers: ['barium ion', 'Barium ion'],
                  tip: 'Look up the element name if needed and that would be the first word to type. Next, include the word “ion”.',
                },
                {
                  id: 'q-002',
                  order: 7,
                  type: 'Input',
                  equation: 'H<sup>+</sup>',
                  groupedItem: true,
                  validAnswers: ['hydrogen ion', 'Hydrogen ion'],
                  tip: 'Look up the element name if needed and that would be the first word to type. Next, include the word “ion”.',
                },
                {
                  order: 8,
                  type: 'Header',
                  text: 'Naming of Anions',
                },
                {
                  order: 9,
                  type: 'Text',
                  text: 'All <b>nonmetal</b> ions in the main group start similar to the element name, but end with the suffix <b>-ide</b> followed by the word “ion”.',
                },
                {
                  id: 'q-003',
                  order: 10,
                  type: 'Input',
                  directions:
                    'Write the name for the following main group anions:',
                  equation: 'Se<sup>2-</sup>',
                  validAnswers: ['selenide ion', 'Selenide ion'],
                  tip: 'Look up the element name if needed. Since this is an anion, the last syllable of the element must be replaced with -ide. Last, include the word “ion”.',
                },
                {
                  id: 'q-004',
                  order: 11,
                  type: 'Input',
                  equation: 'P<sup>3-</sup>',
                  groupedItem: true,
                  validAnswers: ['phosphide ion', 'phosphide ion'],
                  tip: 'Look up the element name if needed and that would be the first word to type. Next, include the word “ion”.',
                },
              ],
            },
            {
              name: 'Transition Metals',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Transition Metals and Post-Transition Metals',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Transition metals and the post-transition metals are the elements highlighted in the dark blue box. These elements are separated from the main group because the method that is used to determine their charge does not follow the numbering system listed 1 through 8. Instead, most of the transition metals have a stable ion with a 2+ charge. However, what is unique in this section is that the elements could take on other charge values and still be very stable, such as iron or chromium. Not all charged transition metals are listed in the dark blue box, only the most common ones are shown. To make this section a bit more curious, not all transition metals take on a variety of charges. Zinc, silver and cadmium are examples of cations transition metals that hold only one charge value; they are monoatomic.',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/transition-metals.jpg',
                  imageAlt:
                    'A periodic table is presented with elements in their ionic form in their respective location in the table. The columns 1 though 8 are highlighted. The transition metals and post-transition metals are highlights in a dark blue box. The box is not completely rectangular. On the top right of the boxed area, the dark blue box outlinesand contains post-transition elements such as tin (Sn), lead (Pb) and bisuth (Bi). Within this boxed section, some elements appear listed once like zinc is as Zn^2+. while others have two ionic forms listed within it element location, such as cobalt, as Ca^2+ and Co^3+.',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Header',
                  text: 'Naming Variable Charge Metal Ions',
                },
                {
                  order: 5,
                  type: 'Text',
                  text: 'If a transition or post-transition metal element can hold more than one charge value, its charge value must be included as a roman numeral alongside its name. The element name would first be written, followed by a roman numeral that represents its charge value and then last, the word “ion”.',
                },
                {
                  id: 'q-005',
                  order: 6,
                  type: 'Input',
                  directions: 'Write the name of the transition metal cations:',
                  equation: 'Co<sup>2+</sup>',
                  validAnswers: ['cobalt(II) ion', 'Cobalt(II) ion'],
                  tip: 'Look up the element name if needed. There is no typed space between the roman numeral and element. The roman numeral must be capitalized. Last, include the word “ion”.',
                },
                {
                  id: 'q-006',
                  order: 7,
                  type: 'Input',
                  equation: 'Bi<sup>4+</sup>',
                  groupedItem: true,
                  validAnswers: ['bismuth(IV) ion', 'Bismuth(IV) ion'],
                  tip: 'Look up the element name if needed. There is no typed space between the roman numeral and element. The roman numeral must be capitalized. Last, include the word “ion”.',
                },
              ],
            },
            {
              name: 'Polyatomic Ions',
              order: 4,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Polyatomic Ions',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Polyatomic ions are a group of atoms that together hold a charge value. Naming polyatomic ions unfortunately typically turns into a memorization task. Here is a table of common polyatomic ions to learn. What is noteworthy is that most polyatomic ions are anions. Therefore, the names have notable suffix endings, such as <b>-ate</b> or <b>-ite</b>. There are a few that end in <b>-ide</b>, which are further down in this table. Only one example of a polyatomic ion using a roman numeral is listed, pertaining to a cation (found in the last row). The roman numeral helps to distinguish mercury(I) ion (a diatomic ion) from mercury(II) ion (a monoatomic atom).',
                },
                {
                  order: 6,
                  type: 'Information Table',
                  headers: ['Chemical Formula', 'Polyatomic Ion Name'],
                  rows: [
                    { id: 1, cells: ['C<sub>2</sub>H<sub>3</sub>O<sub>2</sub><sup>-</sup>', 'acetate ion'] },
                    { id: 2, cells: ['SO<sub>4</sub><sup>2-</sup>', 'sulfate ion'] },
                    { id: 3, cells: ['HSO<sub>4</sub><sup>-</sup>', 'hydrogen sulfate ion'] },
                    { id: 4, cells: ['SO<sub>3</sub><sup>2-</sup>', 'sulfite ion'] },
                    { id: 5, cells: ['HSO<sub>3</sub><sup>-</sup>', 'hydrogen sulfite ion'] },
                    { id: 6, cells: ['NO<sub>3</sub><sup>-</sup>', 'nitrate ion'] },
                    { id: 7, cells: ['NO<sub>2</sub><sup>-</sup>', 'nitrite ion'] },
                    { id: 8, cells: ['CO<sub>3</sub><sup>2-</sup>', 'carbonate ion'] },
                    {
                      id: 9,
                      cells: ['HCO<sub>3</sub><sup>-</sup>', 'bicarbonate or hydrogen carbonate ion'],
                    },
                    { id: 10, cells: ['PO<sub>4</sub><sup>3-</sup>', 'phosphate ion'] },
                    { id: 12, cells: ['HPO<sub>4</sub><sup>2-</sup>', 'hydrogen phosphate ion'] },
                    { id: 12, cells: ['H<sub>2</sub>PO<sub>4</sub><sup>-</sup>', 'dihydrogen phosphateion'] },
                    
                    { id: 13, cells: ['ClO<sub>4</sub><sup>-</sup>', 'perchlorate ion'] },
                    { id: 14, cells: ['ClO<sub>3</sub><sup>-</sup>', 'chlorate ion'] },
                    { id: 15, cells: ['ClO<sub>2</sub><sup>-</sup>', 'chlorite ion'] },
                    { id: 16, cells: ['ClO<sup>-</sup>', 'hypochlorite ion'] },
                    { id: 17, cells: ['CrO<sub>4</sub><sup>2-</sup>', 'chromate ion'] },
                    { id: 18, cells: ['Cr<sub>2</sub>O<sub>7</sub><sup>2-</sup>', 'dichromate  ion'] },
                    { id: 19, cells: ['C<sub>2</sub>O<sub>4</sub><sup>2-</sup>', 'oxalate ion'] },
                    { id: 20, cells: ['SCN<sup>-</sup>', 'thiocyanate ion'] },
                    { id: 21, cells: ['MnO<sub>4</sub><sup>-</sup>', 'permanganate ion'] },
                    { id: 22, cells: ['CN<sup>-</sup>', 'cyanide ion'] },
                    { id: 23, cells: ['OH<sup>-</sup>', 'hydroxide ion'] },
                    { id: 24, cells: ['O<sub>2</sub><sup>2-</sup>', 'peroxide ion'] },
                    { id: 25, cells: ['NH<sub>4</sub><sup>+</sup>', 'ammonium ion'] },
                    { id: 26, cells: ['Hg<sub>2</sub><sup>2+</sup>', 'mercury(I)  ion'] },
                  ],
                },
                {
                  id: 'q-007',
                  order: 5,
                  type: 'Input',
                  directions: 'Write the name of the polyatomic ions:',
                  equation: 'OH<sup>-</sup>',
                  validAnswers: ['hydroxide ion', 'Hydroxide ion'],
                  tip: 'Look up the polyatomic ion in the table if needed. <p> A common misconception for this polyatomic ion is that when one thinks of "hydroxide", students may write the hydrogen first, like HO<sup>-</sup>. This is not correct. This polyatomic ion is always written with the oxygen first.<p>Don\'t forget the word ion!</p>',
                },
                {
                  id: 'q-008',
                  order: 6,
                  type: 'Input',
                  equation: 'BrO<sub>3</sub><sup>-</sup>',
                  groupedItem: true,
                  validAnswers: ['bromate ion', 'Bromate ion'],
                  tip: "Look up the polyatomic ion in the table if needed, but if it is not found, look for a possible pattern. For example, chlorite ion, bromite ion and iodite ion would all have the same number of oxygen ions and charge value since the halogen groups carry similar characteristics. Therefore, their names all have the same suffix ending.<p>Don't forget the word ion!</p>",
                },
              ],
            },
          ],
        },
        {
          name: 'Ionic Compounds',
          order: 2,
          steps: [
            {
              name: 'Ionic Compounds',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Ionic Compounds',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Ionic compounds consist of metal and nonmetal ions. The combination of cations and anions will lead to ionic bonds and a neutral compound. For example,  ',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/NaCl.jpg',
                  imageAlt:
                    'The chemical equation of the foromation of sodium chloride is written in words and in symbols. The words say "sodium ion plus chloride ion combine to form sodium chloride." Above that is presented Na^+ + Cl^- right arrow NaCl.',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'How ions combine begins with the transfer of an electron(s) where the metal ion passes its electron(s) to the nonmetal. As long as all positive charge values equal to all negative charge values, an ionic compound exists.',
                },
                {
                  order: 5,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/MgCl2.jpg',
                  imageAlt:
                    'The chemical equation of the foromation of magnesium chloride is written in words and in symbols. The words say "magnesium ion plus two chloride ions combine to form magnesium chloride." Above that is presented Mg^2+ + 2Cl^- right arrow MgCl2.',
                  centered: true,
                },
              ],
            },
            {
              name: 'Ionic Compound Formulas',
              order: 2,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Ionic Compound Formulas',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'An ionic compound formula is typically written with the cation first followed by the anion. The number of ions or polyatomic ions present are always numbered as subscripts to the right of the element or the parenthesis surrounding a polyatomic ion. When there is only one ion, then number one is never listed. For example,',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/formulas.jpg',
                  imageAlt:
                    'Two examples are provided. The one on the left is MgCl2. Below it is one arrow pointing down to "1 X Mg^2+" and another arrow pointing down to "2 x Cl^-". The other example is (NH4)2S. Below it is one arrow pointing down to "2 X NH4^+" and another arrow pointing down to "1 x S^2-".',
                  centered: true,
                },
                {
                  id: 'q-009',
                  order: 4,
                  type: 'Input',
                  directions:
                    'Create the formula for an ionic compound consisting of the following ions. <em>For typing your answer, write the subscript value in the same line as the symbol. For example, water = H2O.</em>',
                  equation: 'K<sup>+</sup> and S<sup>2-</sup>',
                  validAnswers: ['K2S'],
                  tip: 'The positive and negative charge values must equal each other. Since there are +1 and -2 charges, there must be two +1 ions needed in order for the positive and negative charge values to equal each other and add up to zero (a neutral compound).',
                },
                {
                  id: 'q-010',
                  order: 4,
                  type: 'Input',
                  equation: 'Mn<sup>2+</sup> and OH<sup>-</sup>',
                  groupedItem: true,
                  validAnswers: ['Mn(OH)2'],
                  tip: 'The polyatomic ion is a group that has a charge for the group of atoms, hence a parenthesis is needed to keep the negative charge enveloping the polyatomic ion. Since there is +2 and -1, there must be two -1 ions needed for the positive and negative charge values to equal each other and add up to zero (a neutral compound).',
                },
              ],
            },
            {
              name: 'Naming Ionic Compounds',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Naming Ionic Compounds',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: "Naming ionic compounds follow the same rules as naming the ions present, except for the word “ion” is removed. <ul><li>The cation is always written first followed by the anion.</li><li>The anion will end with the suffix <b>-ide</b>.</li><li>A roman numeral will be present for any cation that could take more than one charge value (specific to the transition metals and post-transition metals).</li></ul> The ionic compound name will follow this pattern:",
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName:
                    '/images/nomenclature/naming-ionic-compounds.jpg',
                  imageAlt:
                    'Describing the image left to right. A green box with curved edges reads "first element". A line extends from its right side and a light green box enveloped in a parenthesis reads "Possible Roman Numberal". Another green box with curved edges is to the right and reads "second element". A line extends from its right side and the ending appears as "ide".',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'We can categorize ionic compound names into three types. They are each provided in the naming below.',
                },
                {
                  id: 'q-011',
                  order: 5,
                  type: 'Input',
                  directions: 'Name the ionic compounds:',
                  equation: 'CsF',
                  validAnswers: ['cesium fluoride', 'Cesium fluoride'],
                  tip: "<p>1. Check what type of cation this is: a main group or a transition metal. This distinction will guide you whether the roman numeral is written or not.</p><p>2. Check your spelling of fluorine is written with 'uo'.</p><p>3. Make sure your anion has the proper suffix because it is an anion.</p><p>4. Do not write “ion”.</p>",
                },
                {
                  id: 'q-012',
                  order: 6,
                  type: 'Input',
                  equation: 'Cu<sub>2</sub>S',
                  groupedItem: true,
                  validAnswers: [
                    'copper(I) sulfide',
                    'Copper(I) sulfide',
                    'copper(I) sulphide',
                    'Copper(I) sulphide',
                  ],
                  tip: "<p>1. Check what type of cation this is: a main group or a transition metal. This distinction will guide you whether the roman numeral is written or not.</p><p>2. Just because there are 2 copper ions does not mean the charge is 2+. Instead, you have to work from the anion since anions never change their charge value.</p><ul><li>Therefore, determine the charge of the sulfide ion.</li><li>If there are 2 copper atoms with an unknown charge, determine copper's single ion charge by ensuring all positive and negative charges add to zero.</li></ul><p></p><p>2. Do not have a space between the copper and its capitalized roman numeral that is in parentheses.</p><p>3. Make sure your anion has the proper suffix because it is an anion. </p><p>4. Do not write “ion”.</p>",
                },
                {
                  id: 'q-013',
                  order: 7,
                  type: 'Input',
                  equation: 'Fe(NO<sub>3</sub>)<sub>3</sub>',
                  groupedItem: true,
                  validAnswers: ['iron(III) nitrate', 'Iron(III) nitrate'],
                  tip: "<p>1. Check what type of cation this is: a main group or a transition metal. This distinction will guide you whether the roman numeral is written or not.</p><p>2. Work from the anion since anions <b>never</b> change their charge value.</p><ul><li>Therefore, determine the charge of the nitrate ion.</li><li>The three beyond the parenthesis of nitrate tells you there are three nitrate ions.</li><li>Multiply the charge of nitrate by the number of nitrate ions. That equals the total negative charge.</li><li>Ensure you select the positive charge value for iron that matches the total negative charges in order for the charges to add to zero.</li></ul><p></p><p>2. Do not have a space between the iron and its capitalized roman numeral that is in parentheses.</p><p>3. Make sure your anion has the proper suffix given in the name of the polyatomic ion.</p><p>4. Do not write “ion”.</p>",
                },
              ],
            },
          ],
        },
        {
          name: 'Molecular Compounds',
          order: 3,
          steps: [
            {
              name: 'Molecular Compounds',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Molecular Compounds',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'The elements involved in a molecular compound are either all nonmetals or metalloids and nonmetals. While there are several  molecular compounds with common names, like water or methane, most follow a nomenclature system.',
                },
              ],
            },
            {
              name: 'Naming Molecular Compounds',
              order: 2,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Naming Molecular Compounds',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'To name a molecular compound, the number of atoms for each element are counted and its number represented as a Greek prefix added to the element name.',
                },
                {
                  order: 3,
                  type: 'Information Table',
                  headers: ['Greek Prefix', 'Number Represented'],
                  rows: [
                    { id: 1, cells: ['mono-', '1'] },
                    { id: 2, cells: ['di-', '2'] },
                    { id: 3, cells: ['tri-', '3'] },
                    { id: 4, cells: ['tetra-', '4'] },
                    { id: 5, cells: ['penta-', '5'] },
                    { id: 6, cells: ['hexa-', '6'] },
                    { id: 7, cells: ['hepta-', '7'] },
                    { id: 8, cells: ['octa-', '8'] },
                    { id: 9, cells: ['nona-', '9'] },
                    { id: 10, cells: ['deca-', '10'] },
                  ],
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'The first element will end as the name of the element, while the second element will end the compound name with <b>-ide</b>. The molecular compound name has this pattern:',
                },
                {
                  order: 5,
                  type: 'Image',
                  imageFileName:
                    '/images/nomenclature/naming-molecular-compounds.jpg',
                  imageAlt:
                    'Describing the image left to right. The word "prefix" has a short blue line extending to its right meeting a blue box with curved edges that says "first elemnt". To the right of this is another word "prefix" that has a short blue line extending to its right meeting a blue box with curved edges that says "second elemnt". A blue line extends from its right side and the ending appears as "ide".',
                  centered: true,
                },
                {
                  order: 6,
                  type: 'Header',
                  text: 'Exception',
                },
                {
                  order: 7,
                  type: 'Text',
                  text: 'One exception to this name pattern involves to the prefix <b>mono-</b>. The first element will never use the prefix <b>mono-</b> when there is one atom. This rule does not apply to the second element.',
                },
                {
                  id: 'q-014',
                  order: 8,
                  type: 'Input',
                  directions: 'Name the molecular compounds:',
                  equation: 'NO<sub>2</sup>',
                  validAnswers: ['nitrogen dioxide', 'Nitrogen dioxide'],
                  tip: 'The systematic naming of molecular compounds involves adding the Greek prefixes, however, not for the use of <b>mono-</b> for the first element. Ensure that the second element ends with <b>-ide</b>.',
                },
                {
                  id: 'q-015',
                  order: 8,
                  type: 'Input',
                  equation: 'ClO<sub>2</sup>',
                  groupedItem: true,
                  validAnswers: ['chlorine dioxide', 'Chlorine dioxide'],
                  tip: 'The systematic naming of molecular compounds involves adding the Greek prefixes, however, not for the use of <b>mono-</b> for the first element. Ensure that the second element ends with <b>-ide</b>.',
                },
                {
                  id: 'q-016',
                  order: 9,
                  type: 'Input',
                  equation: 'NBr<sub>3</sup>',
                  groupedItem: true,
                  validAnswers: ['nitrogen tribromide', 'Nitrogen tribromide'],
                  tip: 'The systematic naming of molecular compounds involves adding the Greek prefixes, however, not for the use of <b>mono-</b> for the first element. Ensure that the second element ends with <b>-ide</b>.',
                },
              ],
            },
            {
              name: 'Homonuclear molecules',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Homonuclear molecules',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: "Homonuclear refers to molecules made of one element with more than 1 atom. The following list refers to labeling a molecule based on the number of its atoms. <ul><li>2 atoms = diatomic</li><li>3 atoms = triatomic</li><li>4 atoms = tetratomic</li><li>6 atoms = hexatomic</li><li>8 atoms = octatomic</li></ul>We do not use those labels however when naming a homonuclear molecule. Instead, all homonuclear molecules are named as the element name itself. For example, H<sub>2</sub> is called hydrogen and P<sub> 4</sub>  is called phosphorus. While this can seem confusing when trying to distinguish a single hydrogen atom from the molecular hydrogen, including the word “atom” and “molecular” is suitable, but not required. The most common homonuclear molecules are listed in the table below:&lt;/",
                },
                {
                  order: 3,
                  type: 'Information Table',
                  headers: [
                    'Diatomic Molecular Formulas',
                    'Diatomic Molecule Name',
                  ],
                  rows: [
                    { id: 1, cells: ['H<sub>2</sub>', 'hydrogen'] },
                    { id: 2, cells: ['N<sub>2</sub>', 'nitrogen'] },
                    { id: 3, cells: ['O<sub>2</sub>', 'oxygen'] },
                    { id: 4, cells: ['F<sub>2</sub>', 'fluorine'] },
                    { id: 5, cells: ['Cl<sub>2</sub>', 'chlorine'] },
                    { id: 6, cells: ['Br<sub>2</sub>', 'bromine'] },
                    { id: 7, cells: ['I<sub>2</sub>', 'iodine'] },
                  ],
                },
                {
                  id: 'q-017',
                  order: 4,
                  type: 'Input',
                  directions: 'Name the molecular compounds:',
                  equation: 'P<sub>4</sup>',
                  validAnswers: ['phosphorus', 'molecular phosphorus'],
                  tip: 'This is a homonuclear molecule. Thus, the tetratomic molecule will be named the same as the name of the element itself.',
                },
              ],
            },
          ],
        },
        {
          name: 'Hydrates',
          order: 4,
          steps: [
            {
              name: 'Hydrates',
              order: 1,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Hydrates',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'Hydrates are typically ionic compounds with water bonded to it by a coordinate covalent bond (a topic studied in the next semester of General Chemistry). An ionic compound without water molecules attached is the anhydrous form of the crystal, while the hydrated form is the hydride form. The molecular formula of a hydrate shows an ionic compound followed by the number of water molecules. There is a dot symbol separating the ionic compound from the water molecules. For example, ',
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/hydrate.jpg',
                  imageAlt: 'MgSO4 dot 7 H2O',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'Unless it is specified as anhydrous, many ionic compounds could be in a hydrated form, but the waters may not be revealed when writing the compound\'s formula. It depends on the necessity of including the waters.',
                },
              ],
            },
            {
              name: 'Naming Hydrates',
              order: 2,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Naming Hydrates',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: "If water is present in the chemical structure, we name the hydrate as follows:<ul><li>(1) Name the ionic compound by following the system rules for ionic compounds</li></ul><ul><li>(2) Water will be named as \"hydrate\"</li></ul><ul><li>(3) The number of water will be identified using the greek prefixes: mono-, di-, tri-, tetra-, penta-, hexa-, etc.</li></ul>",
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/hydrate.jpg',
                  imageAlt: 'MgSO4 dot 7 H2O',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Text',
                  text: 'The hydrate above is named as magnesium sulfate heptahydrate. ',
                },
                {
                  id: 'q-018',
                  order: 5,
                  type: 'Input',
                  directions: 'Name the hydrates:',
                  equation: 'Na<sub>2</sub>CO<sub>3</sub>∙10H<sub>2</sub>O',
                  validAnswers: [
                    'sodium carbonate decahydrate',
                    'Sodium carbonate decahydrate',
                    'Sodium Carbonate Decahydrate',
                  ],
                  tip: 'Review your Greek prefixes and polyatomic ions if needed. The ionic compound is named first, followed by the Greek prefix “hydrate”. There is no space between the Greek prefix and hydrate. No need to capitalize any letter the Greek prefix or the hydrate.',
                },
                {
                  id: 'q-019',
                  order: 6,
                  type: 'Input',
                  equation: 'CoCl<sub>2</sub>∙6H<sub>2</sub>O',
                  groupedItem: true,
                  validAnswers: [
                    'cobalt(II) chloride hexahydrate',
                    'Cobalt(II) chloride hexahydrate',
                    'cobalt(II) Chloride Hexahydrate',
                  ],
                  tip: 'Watch out that cobalt is a transition metal and will require a Roman numeral since it can take on variable positive charge. The Roman numeral is always written without a space right after the element name. It is also capitalized Roman numerals.',
                },
              ],
            },
          ],
        },
        {
          name: 'Acids',
          order: 5,
          steps: [
            {
              name: 'Acids',
              order: 2,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Acids',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'An acid is a molecular compound that has a donable hydrogen ion (not all hydrogen ions are donable!). Once donated, the solution consists of the hydrogen ion and anion. The following discusses the rules for naming an acid based on if the anion contains an oxygen as part of a polyatomic ion or not.',
                },
              ],
            },
            {
              name: 'Oxyacids',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Oxyacids',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: "An oxyacid refers to an acid that has a polyatomic ion containing one or more oxygen atoms. The name of the acid can be determined from a series that dependent on the number of oxygen atoms. The acid will end either with <b>-ic</b> or <b>-ous</b> followed by <b>acid</b>. <ul><li>The polyatomic ion in the series with one more oxygen will end in <b>-ate</b> and so its acid will end in <b>-ic acid</b>.</li><li> The other polyatomic ion in the series with have one less oxygen and will end in <b>-ite</b> and so its acid will end in <b>-ous acid</b>.</li></ul><p>Review the figure below and follow the examples that have arrows that lead from <b>-ate</b> to <b>-ic acid</b> and <b>-ite</b> to <b>-ous acid</b>.</p>",
                },
                {
                  order: 3,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/oxyacids-1.jpg',
                  imageAlt:
                    'A table is shown with three columns and three rows. The top row first cell is empty. The top row second column has "-ate right arrow -ic acid". The cell\'s contents are highlighted in a light green box. The top row third colum has "-ite right arrow -ous acid". The cell\'s contents are highlighted in a light pink box. The second row first column has "polyatomic ion" and below it reads "acid form". The second row second column has the polyatomic ion formula SO4^2- equals sulfate ion. Below it, is the acid formulas H2SO4 = sulfuric acid. The subscript 4 is highlighted in a small blue circle. The "ate" and the the "ic" in sulfate and sulfuric are highlighted in a light green box and and green arrow points from "ate" to "ic". The second row third column has the polyatomic ion formula SO3^2- equals sulfite ion. Below it, is the acid formulas H2SO3 = sulfurous acid. The subscript 3 is highlighted in a small light purple circle. The "ite" and the "ous" in sulfite and sulfurous are highlighted in a light pink box and and a red arrow points from "ite" to "ous". The third row first column has "polyatomic ion" and below it reads "acid form". The third row second column has the polyatomic ion formula NO3^- equals nitrate ion. Below it, is the acid formulas HNO3 = nitric acid. The subscript 4 is highlighted in a small blue circle. The "ate" and the the "ic" in nitrate and nitric are highlighted in a light green box and and green arrow points from "ate" to "ic". The third row third column has the polyatomic ion formula NO2^- equals nitrite ion. Below it, is the acid formulas HNO2 = nitrous acid. The subscript 2 is highlighted in a small light purple circle. The "ite" and the "ous" in nitrite and nitrous are highlighted in a light pink box and and a red arrow points from "ite" to "ous".',
                  centered: true,
                },
                {
                  order: 4,
                  type: 'Text',
                  text: '<p>Notice that nitrate ion, which has 3 oxygen atoms, when it combines with a hydrogen ion, will become nitric acid. Similarly, the sulfate ion, which has 4 oxygen atoms, when it combines with a hydrogen ion, will become sulfuric acid. While these polyatomic ions do not have the same number of oxygen atoms, they have one more oxygen than the polyatomic acid in their respective series.</p>',
                },
                {
                  order: 5,
                  type: 'Text',
                  text: 'Halogens have a larger (4 chemical) series of oxyacids. For example:',
                },
                {
                  order: 6,
                  type: 'Image',
                  imageFileName: '/images/nomenclature/oxyacids-2.jpg',
                  imageAlt:
                    'A table is shown with three columns and three rows. The top row first cell is empty. The top row second column has "-ate right arrow -ic acid". The cell\'s contents are highlighted in a light green box. The top row third colum has "-ite right arrow -ous acid". The cell\'s contents are highlighted in a light pink box. The second row first column has "polyatomic ion" and below it reads "acid form". The second row second column has the polyatomic ion formula ClO4^- equals perchlorate ion. Below it, is the acid formulas HClO4 = perchloric acid. The subscript 4 is highlighted in a small blue circle and the prefix "per-" is also highighted in a blue circle. The "ate" and the the "ic" in perchlorate and perchloric are highlighted in a light green box and green arrow points for "ate" to "ic". The second row third column has the polyatomic ion formula ClO^- equals chlorite ion. Below it, is the acid formula HClO2 = chlorous acid. The subscript 2 is highlighted in a small light purple circle. The "ite" and the "ous" in chlorite and chlorous are highlighted in a light pink box and and a red arrow points from "ite" to "ous". The third row first column has "polyatomic ion" and below it reads "acid form". The third row second column has the polyatomic ion formula ClO3^- equals chlorate ion. Below it, is the acid formulas HClO3 = chloric acid. The subscript 3 is highlighted in a small blue circle. The "ate" and the the "ic" in chlorate and chloric are highlighted in a light green box and green arrow points for "ate" to "ic". The third row third column has the polyatomic ion formula ClO^- equals hypochlorite ion. Below it, is the acid formulas HClO = hypochlorous acid. A small empty light purple circle appears to the lower right of the oxygen in ClO^-. In addition, the prefix "hypo" in hypochlorite and hypochlorous is highlighted with a light purple circle. The "ite" and the "ous" in hypochlorite and hypochlorous are highlighted in a light pink box and and a red arrow points from "ite" to "ous".',
                  centered: true,
                },
                {
                  order: 7,
                  type: 'Text',
                  text: 'Notice the additional prefix added for the species with the most number of oxygens and a prefix added for the species with the least number of oxygens.',
                },
                {
                  id: 'q-020',
                  order: 8,
                  type: 'Input',
                  directions: 'Name the acid:',
                  equation: 'H<sub>2</sub>CO<sub>3</sub>',
                  validAnswers: [
                    'carbonic acid',
                    'Carbonic acid',
                    'Carbonic Acid',
                  ],
                  tip: 'It is a good assumption that if a molecule begins with a hydrogen, it is an acid. <p>Notice that the acid is an oxyacid. Look up the polyatomic ion if needed to find its name: CO<sub>3</sub><sup>2-</sup>.</p><p>Look at the ending of the ion name and determine if the acid form will end in <b>-ic acid</b> or <b>-ous acid</b>.',
                },
                {
                  id: 'q-021',
                  order: 8,
                  type: 'Input',
                  equation: 'H<sub>3</sub>PO<sub>4</sub>',
                  groupedItem: true,
                  validAnswers: [
                    'phosphoric acid',
                    'Phosphoric acid',
                    'Phosphoric Acid',
                  ],
                  tip: 'If needed, look up the name for the polyatomic ion, PO<sub>4</sub><sup>3-</sup>. <p>While the "phosph" may be the root, the acid ending will come after "phosphor".</p>',
                },
              ],
            },
            {
              name: 'Binary Aqueous Acids',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Binary Aqueous Acids',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'All acids could be considered being aqueous, but there is a distinction in naming binary acids when they are aqueous or gaseous. We will first review the binary aqueous acids.',
                },
                {
                  order: 3,
                  type: 'Text',
                  text: 'A binary acid has the combination of a two elements in ionic form: hydrogen ion and one other element in anionic form. While binary refers to a total of 2 elements, there are are a few exceptions where the anion could actually be more than one element, but its name will still follow the binary aqueous acid naming rule. Due to these exceptions, another way to view naming aqueous acids that are not oxyacids (and fall under the binary acids category) is to use the following naming system for any acid that does not contan any oxygen.',
                },
                {
                  order: 4,
                  type: 'Header',
                  text: 'Naming Binary Aqueous Acids',
                },
                {
                  order: 5,
                  type: 'Text',
                  text: 'The acid name will start with <b>hydro-</b> and will have its suffix replaced with <b>-ic acid</b>. Review the table below for examples.',
                },
                {
                  order: 6,
                  type: 'Information Table',
                  headers: ['Aqueous Acid Formulas', 'Acid Name'],
                  rows: [
                    { id: 1, cells: ['HF(aq)', 'hydrofluoric acid'] },
                    { id: 2, cells: ['HCl(aq)', 'hydrochloric acid'] },
                    { id: 3, cells: ['H<sub>2</sub>S(aq)', 'hydrosulfuric acid'] },
                    { id: 4, cells: ['HCN(aq)', 'hydrocyanic acid'] },
                  ],
                },
                {
                  order: 7,
                  type: 'Text',
                  text: 'Notice the last two example in the table. Hydrosulfuric acid is not quite replacing the <b>sulf</b> in sulfite ion. We write the full element name hydro<b>sulfur</b>ic acid. Ih the last row, the anion, CN<sup>-</sup> has two elements, neither being oxygen. If there were an oxygen, such as CNO<sup>-</sup>, the cyanate ion, could likely be cyanic acid when in acid form. For HCN, we utilize the binary aqueous acid naming rule.',
                },
                {
                  id: 'q-022',
                  order: 8,
                  type: 'Input',
                  directions: 'Name the acids:',
                  equation: 'HBr(aq)',
                  validAnswers: [
                    'hydrobromic acid',
                    'Hydrobromic  acid',
                    'Hydrobromic  Acid',
                  ],
                  tip: 'It is a good assumption that if a molecule begins with a hydrogen, it is an acid. <p>This a binary acid and the aqueous state is provided. Follow the rule as all other binary aqueous acids.',
                },
                {
                  id: 'q-023',
                  order: 9,
                  type: 'Input',
                  equation: 'HI(aq)',
                  groupedItem: true,
                  validAnswers: [
                    'hydroiodic acid',
                    'Hydroiodic acid',
                    'Hydroiodic Acid',
                  ],
                  tip: 'Watch out how you spell the ending for the replacement of the iodide ion.',
                },
              ],
            },
            {
              name: 'Binary Gaseous Acids',
              order: 3,
              contentBlocks: [
                {
                  order: 1,
                  type: 'Header',
                  text: 'Naming Binary Gaseous Acids',
                },
                {
                  order: 2,
                  type: 'Text',
                  text: 'If a binary acid is in gaseous form, we name the acid differently than if it were aqueous.',
                },
                {
                  order: 3,
                  type: 'Text',
                  text: 'The acid name will follow similarly to a molecular compound, but there are differences. You will name the first element, <b>hydrogen</b>, and then name the second element, changing its ending to <b>-ide</b>. Do not include the word acid. There will be no Greek prefixes added either.',
                },
                {
                  order: 4,
                  type: 'Information Table',
                  headers: ['Gaseous Acid Formulas', 'Acid Name'],
                  rows: [
                    { id: 1, cells: ['HF(g)', 'hydrogen fluoride'] },
                    { id: 2, cells: ['HCl(g)', 'hydrogen chloride'] },
                    { id: 3, cells: ['H<sub>2</sub>S(g)', 'hydrogen sulfide'] },
                    { id: 4, cells: ['HCN(g)', 'hydrogen cyanide'] },
                  ],
                },
                {
                  order: 5,
                  type: 'Text',
                  text: 'If an acid is not labeled with its state of matter alongside it, then the ambiguity gives us the discretion on how to name the acid, choosing either the aqueous or gaseous rules. Both names would be considered correct.',
                },
                {
                  id: 'q-024',
                  order: 6,
                  type: 'Input',
                  directions: 'Name the acids:',
                  equation: 'HBr(g)',
                  validAnswers: [
                    'hydrogen bromide',
                    'Hydrogen bromide',
                    'Hydrogen Bromide',
                  ],
                  tip: 'Follow the rule for a binary gaseous acid.',
                },
                {
                  id: 'q-025',
                  order: 7,
                  type: 'Input',
                  equation: 'HI(g)',
                  groupedItem: true,
                  validAnswers: [
                    'hydrogen iodide',
                    'Hydrogen iodide',
                    'Hydrogen Iodide',
                  ],
                  tip: 'Watch out how you spell the iodide ion.',
                },
              ],
            },
          ],
        },
      ],
    },
  },
  rank: 5,
};

export default chemicalNomenclature;
